const MAX_LOG_ENTRIES = 500;

export interface AnyCommand {
  type: string;
  [key: string]: unknown;
}

export interface CommandActor {
  type: 'user' | 'agent' | 'multiplayer';
  name?: string;
}

export interface CommandLogEntry {
  command: AnyCommand;
  timestamp: number;
  actor: CommandActor;
  /** Number of coalesced repeated commands (1 = no coalescing). */
  coalescedCount: number;
}

export interface CommandLog {
  push(entry: Omit<CommandLogEntry, 'coalescedCount'>): void;
  getEntries(): readonly CommandLogEntry[];
  subscribe(listener: () => void): () => void;
  clear(): void;
}

export function createCommandLog(): CommandLog {
  const entries: CommandLogEntry[] = [];
  let snapshot: readonly CommandLogEntry[] = entries;
  const listeners = new Set<() => void>();

  function notify() {
    snapshot = [...entries];
    for (const fn of listeners) fn();
  }

  return {
    push(entry: Omit<CommandLogEntry, 'coalescedCount'>) {
      // Coalesce consecutive same-type commands
      const last = entries.length > 0 ? entries[entries.length - 1] : null;
      if (last && last.command.type === entry.command.type) {
        last.command = entry.command;
        last.timestamp = entry.timestamp;
        last.coalescedCount += 1;
        notify();
        return;
      }

      entries.push({ ...entry, coalescedCount: 1 });
      if (entries.length > MAX_LOG_ENTRIES) {
        entries.shift();
      }
      notify();
    },

    getEntries() {
      return snapshot;
    },

    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },

    clear() {
      entries.length = 0;
      notify();
    },
  };
}
